import {
  LightBulbIcon,
  RocketLaunchIcon,
  ShieldCheckIcon,
  CogIcon,
} from '@heroicons/react/24/outline'

const differentiators = [
  {
    name: 'Pure Custom Engineering',
    description: 'Every line of code is written specifically for your needs—no WordPress, no Shopify templates, no generic plugins.',
    icon: CogIcon,
    details: [
      'Custom-built applications from scratch',
      'Bespoke architecture designed for your requirements',
      'Purpose-written code, not assembled templates',
      'Tailored solutions that evolve with your business'
    ],
    color: 'purple'
  },
  {
    name: 'Deep Domain Expertise',
    description: 'We don\'t just code - we understand your industry\'s unique challenges, regulations, and opportunities.',
    icon: LightBulbIcon,
    details: [
      'Healthcare: HIPAA compliance, HL7 FHIR, medical device integration',
      'Finance: SOX compliance, PCI DSS, fraud detection algorithms',
      'Research: Bioinformatics pipelines, statistical analysis, data visualization',
      'E-commerce: Payment processing, inventory optimization, customer analytics'
    ],
    color: 'blue'
  },
  {
    name: 'Speed Without Sacrifice',
    description: 'Rapid development cycles that maintain the highest standards of quality and security.',
    icon: RocketLaunchIcon,
    details: [
      'Agile 2-week sprints with continuous client feedback',
      'Pre-built components and architectural patterns',
      'Automated testing and quality assurance',
      'DevOps practices for fast, reliable deployments'
    ],
    color: 'green'
  },
  {
    name: 'Security-First Approach',
    description: 'Every solution is built with security and compliance as core requirements, not afterthoughts.',
    icon: ShieldCheckIcon,
    details: [
      'Security audits and penetration testing',
      'Encryption at rest and in transit',
      'Role-based access control and authentication',
      'Compliance with GDPR, HIPAA, SOX standards'
    ],
    color: 'red'
  }
]

const getColorClasses = (color) => {
  const colors = {
    blue: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', accent: 'bg-blue-100' },
    green: { bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-200', accent: 'bg-green-100' },
    red: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', accent: 'bg-red-100' },
    purple: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200', accent: 'bg-purple-100' }
  }
  return colors[color] || colors.blue
}

export default function WhyChooseUs() {
  return (
    <div className="bg-white py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-2xl lg:text-center">
          <h2 className="text-base/7 font-semibold text-atticusPink-600">Why Choose Atticus Labs</h2>
          <p className="mt-2 text-pretty text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl lg:text-balance">
            The Partner You Need for Mission-Critical Projects
          </p>
          <p className="mt-6 text-lg/8 text-gray-600">
            Beyond just development skills, we bring deep domain expertise, proven methodologies, 
            and a commitment to your long-term success.
          </p>
        </div>

        {/* Main Differentiators */}
        <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {differentiators.map((item, index) => {
              const colorClasses = getColorClasses(item.color)
              return (
                <div key={item.name} className={`${colorClasses.bg} rounded-xl p-8`}>
                  <div className="flex items-start mb-6">
                    <div className={`flex size-12 items-center justify-center rounded-lg bg-white shadow-sm mr-4 flex-shrink-0`}>
                      <item.icon aria-hidden="true" className={`size-6 ${colorClasses.text}`} />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.name}</h3>
                      <p className="text-gray-700 mb-4">{item.description}</p>
                    </div>
                  </div>
                  
                  <ul className="space-y-3">
                    {item.details.map((detail, idx) => (
                      <li key={idx} className="flex items-start text-sm">
                        <span className={`flex items-center justify-center w-6 h-6 rounded-full ${colorClasses.accent} ${colorClasses.text} text-xs font-semibold mr-3 flex-shrink-0`}>
                          {idx + 1}
                        </span>
                        <span className="text-gray-700 pt-0.5">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <p className="text-lg text-gray-600 mb-6">
            Ready to build something exceptional?
          </p>
          <button 
            onClick={() => {
              const scroller = require('react-scroll').scroller;
              scroller.scrollTo('contact', { smooth: true, duration: 500, offset: -70 });
            }}
            className="bg-atticusPink-600 hover:bg-atticusPink-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-300 text-lg"
          >
            Let's Talk About Your Project
          </button>
        </div>
      </div>
    </div>
  )
}